'use client';

import { useEffect, useRef } from 'react';
import { loadBuildId, refreshBuildId } from './build-id';

/** Re-check the server's build id at most this often (a refocus storm - app
 *  switching, notification peeks - must not turn into a fetch per flick). */
const CHECK_INTERVAL_MS = 30_000;
/** Never auto-reload twice within this window (belt-and-braces against a
 *  flapping id producing a reload loop). */
const RELOAD_GUARD_MS = 60_000;
const RELOAD_STAMP_KEY = 'tsr-build-reload';

/**
 * The stale-tab watchdog: when a backgrounded tab comes back to a server
 * running a DIFFERENT build (a deploy, a dev-server restart), reload once so
 * the tab never mixes old assets with new markup (docs/MOBILE.md; the
 * ghost-drawer false alarms of 2026-07-11). The baseline is the id fetched
 * when this page loaded; checks run only on visibility regains, throttled.
 * Renders nothing.
 */
export function BuildWatchdog() {
  const baseline = useRef<string | null>(null);
  const lastCheck = useRef(0);

  useEffect(() => {
    let cancelled = false;
    // The baseline rides the shared memo (build-id.ts) - the device store asks
    // for the same value on the same load; every later check must go to the
    // server again, because a NEW build is exactly what it looks for.
    void loadBuildId().then((id) => {
      if (!cancelled && id) baseline.current = id;
    });

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCheck.current < CHECK_INTERVAL_MS) return;
      lastCheck.current = now;
      void refreshBuildId().then((id) => {
        if (cancelled || !id) return;
        if (baseline.current === null) {
          baseline.current = id; // the load-time fetch lost the race; adopt
          return;
        }
        if (id === baseline.current) return;
        const stamped = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? 0);
        if (now - stamped < RELOAD_GUARD_MS) return;
        sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
        location.reload();
      });
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return null;
}
